import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Icon } from '../theme/Icon';
import { Button } from './Button';
import { LoadingButton } from './LoadingButton';
import { t } from '@app/i18n';
import s from './ConfirmDialog.module.css';

export interface ImpactItem {
  /** O que será afetado ("políticas que citam esta identidade"). */
  label: string;
  /** Contagem concreta — FR-130 exige número, não "vários". */
  count: number;
}

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message?: ReactNode;
  /**
   * Impacto concreto da ação, com contagens (FR-130). Nenhuma cascata é silenciosa:
   * se a ação remove dependências, elas aparecem aqui.
   */
  impact?: ImpactItem[];
  /** `true` = ação destrutiva: botão vermelho e ícone de alerta. */
  destructive?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Pode devolver promise — o botão bloqueia o duplo clique enquanto pendente. */
  onConfirm: () => void | Promise<unknown>;
  onCancel: () => void;
  /** Estado de carregando controlado. */
  loading?: boolean;
  /**
   * Desabilita a confirmação sem esconder o diálogo. Serve ao caso em que a
   * confirmação **não pode ser informada**: o FR-130 manda listar o impacto, e
   * quando esse impacto não pôde ser lido não há decisão a tomar — confirmar
   * seria confirmar às cegas. Use junto de `error`, que diz por quê.
   */
  confirmDisabled?: boolean;
  /** Banner de erro, se a confirmação anterior falhou. */
  error?: ReactNode;
}

/**
 * Diálogo de confirmação (FR-130): lista o impacto com contagens e usa botão
 * vermelho em ação destrutiva. Fecha por Esc; foco inicial no botão de cancelar.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  impact,
  destructive = false,
  confirmLabel = t('common.ui.confirm'),
  cancelLabel = t('common.ui.cancel'),
  onConfirm,
  onCancel,
  loading,
  confirmDisabled = false,
  error,
}: ConfirmDialogProps) {
  const titleId = useId();
  const descId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    cancelRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className={s.overlay} data-testid="confirm-overlay">
      <div
        className={s.mbox}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={message || impact?.length ? descId : undefined}
      >
        <div className={s.mh}>
          <span
            className={[s.hicon, destructive ? '' : s.hiconInfo].filter(Boolean).join(' ')}
            aria-hidden="true"
          >
            <Icon name={destructive ? 'warning' : 'question'} />
          </span>
          <h3 id={titleId}>{title}</h3>
        </div>

        <div className={s.mb} id={descId}>
          {error}
          {typeof message === 'string' ? <p>{message}</p> : message}
          {impact?.length ? (
            <ul className={s.impact}>
              {impact.map((item) => (
                <li className={s.impactRow} key={item.label}>
                  <span
                    className={[s.impactCount, item.count === 0 ? s.impactCountZero : '']
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {item.count}
                  </span>
                  {item.label}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className={s.mf}>
          <Button ref={cancelRef} variant="out" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <LoadingButton
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
            disabled={confirmDisabled}
            loadingLabel="Executando…"
          >
            {confirmLabel}
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}
